// What the ground under a square is, when nothing is standing on it: a vein, a
// heap of scrap, or a belt — and the one line a piece's own card borrows to say
// what it is parked on.
//
// The pure model behind the inspector's ground card, `selection.ts`'s twin.
// Same field names where a field means the same thing (`owner`, `holdings`,
// `progress`), so the card reuses the parts that already draw them.
//
// **Few words**: a swatch and a number say what a sentence would, and the one
// `note` is the rule the square obeys, never rules trivia.

import type { Color, PieceType } from "src/engine/rules/board";
import type { GameState } from "src/engine/rules/commands";
import { areEnemies, pieceAt, type Position } from "src/engine/rules/position";
import { METAL, PURE, SPECIAL } from "src/engine/rules/materials";
import {
  beltDirAt,
  beltOwnerAt,
  blueprintOwnerAt,
  depositAt,
  depositContents,
  depositNameAt,
  depositTierAt,
  depositYieldAt,
  digProgressAt,
  hasBlueprint,
  hasStructure,
  heapAt,
  heapRankAt,
  heapSideAt,
  type IndustryState,
  makeUnit,
  NO_BELT,
  unitLine
} from "src/engine/rules/industry/state";
import { laneAhead, veinTiles } from "src/engine/rules/industry/survey";
import { PIECE_NAMES, RULES } from "src/engine/rules/rules";
import { armyName } from "./armyName";
import { squareLabel } from "./gridLabels";
import { slotCounts, slotName, unitSlot } from "./shelfTotals";

/** The ground line on a card about something standing on it. */
export interface UnderRow {
  slot: number;
  text: string;
}

export interface GroundModel {
  kind: "vein" | "scrap" | "belt" | "blueprint";
  name: string;
  /** `squareLabel`. */
  square: string;
  /** Null for a vein and for scrap: terrain belongs to nobody. */
  owner: "yours" | "ally" | "enemy" | null;
  /** The swatch beside the name. Null for track, which is not a material. */
  slot: number | null;
  /** The headline number — the whole vein, or the whole heap. */
  count: number | null;
  /** This tile's own charge, where the headline is the vein's. */
  here: number | null;
  /** A special's dig, and only once it has started. */
  progress: { fraction: number; ready: boolean } | null;
  /** Belt cargo. Swatch counts, dearest slot first. */
  holdings: { slot: number; count: number }[];
  note: string | null;
  /** Ore under the track. A deposit is terrain, so a lane may run over one. */
  under: UnderRow | null;
  /** Squares the board lights besides this one: the rest of the vein, or the lane ahead. */
  marks: number[];
}

/** What a heap hands up, whatever died there: grade-0 metal (`mineOnce`). */
const SCRAP_SLOT = unitSlot(makeUnit(0, 0, METAL));

/**
 * The line's word, never the rung's — a tile of grade-1 stone is still a stone
 * vein. Two entries, because only the workhorses have a generic name; a special
 * is called what comes up of it.
 */
const VEIN_WORD: readonly string[] = ["Iron", "Stone"];

const veinName = (unit: number): string =>
  `${VEIN_WORD[unitLine(unit)] ?? slotName(unitSlot(unit))} vein`;

/**
 * A mixed tile (§31 D1) is called what it is — "Rich iron deposit" — and a
 * pure one keeps its vein word.
 */
function tileName(industry: IndustryState, sq: number): string {
  if (depositTierAt(industry, sq) === PURE) return veinName(depositYieldAt(industry, sq));
  const name = depositNameAt(industry, sq);
  return name[0].toUpperCase() + name.slice(1);
}

/** "9 iron, 3 cobble" on a mixed tile; the bare count on a pure one. */
function leftText(industry: IndustryState, sq: number): string {
  const contents = depositContents(industry, sq);
  if (depositTierAt(industry, sq) === PURE) return `${depositAt(industry, sq)}`;
  return contents.map(({ unit, count }) => `${count} ${slotName(unitSlot(unit)).toLowerCase()}`).join(", ");
}

/**
 * Whose corpse this is. Unmarked scrap — a crushed belt, a fallen building's
 * stock — nobody died for, so it is only scrap.
 */
function wreckName(industry: IndustryState, sq: number): string {
  const side = heapSideAt(industry, sq);
  const rank = heapRankAt(industry, sq);
  if (side < 0 || rank === 0) return "Scrap";
  return `${armyName(side as Color)} ${PIECE_NAMES[rank as PieceType].toLowerCase()}'s wreck`;
}

/** `selection.ts`'s three words, for the side whose track this is. */
const ownerOf = (
  pos: Position,
  localSide: Color,
  owner: Color
): "yours" | "ally" | "enemy" =>
  owner === localSide ? "yours" : areEnemies(pos, localSide, owner) ? "enemy" : "ally";

/** Everything a kind has nothing to say about. A call, so no two models share an array. */
const blank = (): Omit<GroundModel, "kind" | "name" | "square"> => ({
  owner: null,
  slot: null,
  count: null,
  here: null,
  progress: null,
  holdings: [],
  note: null,
  under: null,
  marks: []
});

/**
 * What is on this square, where it is the square itself you have clicked.
 *
 * Null with a piece or a building on it — that card is theirs — and null on
 * plain ground. Belt before blueprint before scrap before vein: a heap and a
 * belt cannot share a square, and a corpse buries the seam, so the only pair
 * that genuinely coexist is track over ore, which is what `under` is for.
 */
export function groundModel(
  state: GameState,
  square: number,
  localSide: Color
): GroundModel | null {
  const { industry, position } = state;
  if (pieceAt(position, square) !== 0 || hasStructure(industry, square)) return null;
  const at = squareLabel(square);

  if (beltDirAt(industry, square) !== NO_BELT) {
    return {
      ...blank(),
      kind: "belt",
      name: "Belt",
      square: at,
      owner: ownerOf(position, localSide, beltOwnerAt(industry, square) as Color),
      holdings: slotCounts(industry, square, ["cargo"]),
      marks: laneAhead(position, industry, square),
      under: underRow(state, square)
    };
  }

  if (hasBlueprint(industry, square)) {
    return {
      ...blank(),
      kind: "blueprint",
      name: "Blueprint",
      square: at,
      owner: ownerOf(position, localSide, blueprintOwnerAt(industry, square) as Color),
      note: "Waiting for iron.",
      under: underRow(state, square)
    };
  }

  const heap = heapAt(industry, square);
  if (heap > 0) {
    return {
      ...blank(),
      kind: "scrap",
      name: wreckName(industry, square),
      square: at,
      slot: SCRAP_SLOT,
      count: heap,
      note: "A pawn standing here mines it; a queen sweeps it up."
    };
  }

  if (depositAt(industry, square) === 0) return null;
  const unit = depositYieldAt(industry, square);
  const tiles = veinTiles(industry, square);
  const special = unitLine(unit) === SPECIAL;
  const dug = digProgressAt(industry, square);
  const mixed = depositTierAt(industry, square) !== PURE;
  return {
    ...blank(),
    kind: "vein",
    name: tileName(industry, square),
    square: at,
    slot: unitSlot(unit),
    count: tiles.reduce((total, tile) => total + depositAt(industry, tile), 0),
    here: depositAt(industry, square),
    // Only once a pawn has started: a bar at nought claims work nobody is doing.
    progress:
      special && dug > 0 ? { fraction: dug / RULES.specialDigTurns, ready: false } : null,
    note: mixed
      ? `${leftText(industry, square)} left here. ${
          special
            ? `A pawn digs cobble one a turn, the rest one every ${RULES.specialDigTurns} turns.`
            : "A pawn standing here digs one a turn."
        }`
      : special
        ? `A pawn standing here lifts one every ${RULES.specialDigTurns} turns.`
        : "A pawn standing here digs one a turn.",
    marks: tiles.filter((tile) => tile !== square)
  };
}

/**
 * The ground under whatever is standing on this square, for that thing's own
 * card. It asks no questions about the piece — the caller asks precisely
 * because one is there.
 */
export function underRow(state: GameState, square: number): UnderRow | null {
  const { industry } = state;
  const left = depositAt(industry, square);
  if (left > 0) {
    const unit = depositYieldAt(industry, square);
    return {
      slot: unitSlot(unit),
      text: `${tileName(industry, square)} · ${leftText(industry, square)} left here`
    };
  }
  const heap = heapAt(industry, square);
  if (heap > 0) {
    return { slot: SCRAP_SLOT, text: `${wreckName(industry, square)} · ${heap} left` };
  }
  return null;
}
